// Phase enum for the state machine
export enum Phase {
  ONBOARDING = 'ONBOARDING',
  CONTEXT = 'CONTEXT',
  BASELINE = 'BASELINE',
  CLIP_PLAY = 'CLIP_PLAY',
  CLIP_CHECKIN = 'CLIP_CHECKIN',
  SILENCE = 'SILENCE',
  SILENCE_CHECKIN = 'SILENCE_CHECKIN',
  INSIGHT = 'INSIGHT',
  SHIFT_CHOICE = 'SHIFT_CHOICE',
  REFLECTION = 'REFLECTION',
  SUMMARY = 'SUMMARY',
  GUIDANCE = 'GUIDANCE',
}

// Context questionnaire data
export interface ContextData {
  activity: string;
  energy: number;
  stress: number;
  durationMs?: number;
}

// Mood check-in entry
export interface MoodCheckin {
  clipIndex: number;
  mood: number;
  timestamp: number;
  label?: string;
}

// Silence check-in entry
export interface SilenceCheckin {
  silenceIndex: number;
  mood: number;
  timestamp: number;
}

// Reflection answers
export interface Reflections {
  q1: string; // 'no' | 'a_little' | 'clearly'
  q2: number; // 0-10
  q3: number; // 0-10
  note?: string; // free-form text about impact/future use
  durationMs?: number;
}

// Computed metrics
export interface ComputedMetrics {
  reactionStrength: number;
  residualEffect: number;
  shiftEffect: number;
}

// Timeline entry for the chart
export interface TimelineEntry {
  label: string;
  mood: number;
  timestamp: number;
  type: 'baseline' | 'clip' | 'silence';
  index?: number;
}

// Audio clip metadata
export interface Clip {
  id: string;
  title: string;
  filename: string;
  durationSec: number;
  valence: number;
  arousal?: number;
  emotionTag?: string;
  useForShift: boolean;
  useForStay: boolean;
  sortOrder: number;
}

// Guidance template
export interface GuidanceTemplate {
  id: string;
  ruleKey: string;
  tipText: string;
  priority: number;
}

// Main session state
export interface SessionState {
  phase: Phase;
  sessionId: string | null;
  clipIndex: number;
  silenceIndex: number;
  insightIndex: number;
  context: ContextData | null;
  baselineMood: number;
  baselineNote: string;
  clipCheckins: MoodCheckin[];
  silenceCheckins: SilenceCheckin[];
  shiftChoice: 'shift' | 'stay' | null;
  reflections: Reflections | null;
  computedMetrics: ComputedMetrics | null;
  clipTimeline: TimelineEntry[];
  clips: Clip[];
  guidanceTemplates: GuidanceTemplate[];
}

// Action types for state updates
export type Action =
  | { type: 'NEXT_PHASE' }
  | { type: 'SET_CONTEXT'; payload: ContextData }
  | { type: 'SET_BASELINE'; payload: { mood: number; note: string } }
  | { type: 'ADD_CLIP_CHECKIN'; payload: { mood: number; label?: string } }
  | { type: 'ADD_SILENCE_CHECKIN'; payload: { mood: number } }
  | { type: 'SET_SHIFT_CHOICE'; payload: 'shift' | 'stay' }
  | { type: 'SET_REFLECTIONS'; payload: Reflections }
  | { type: 'SET_COMPUTED_METRICS'; payload: ComputedMetrics }
  | { type: 'SET_SESSION_ID'; payload: string }
  | { type: 'SET_CLIPS'; payload: Clip[] }
  | { type: 'SET_GUIDANCE_TEMPLATES'; payload: GuidanceTemplate[] }
  | { type: 'RESTORE_STATE'; payload: SessionState }
  | { type: 'RESET' };

// Dispatch function type
export type Dispatch = (action: Action) => void;
